import { ComponentProps, useCallback, useEffect, useState } from "react"

import {
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
} from "@tabler/icons-react"
import { DayPicker } from "react-day-picker"
import { DateRange, OnSelectHandler } from "react-day-picker"
import { P, match } from "ts-pattern"

import { cn } from "@/lib/utils"

export type CalendarProps = ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  // We control the selected values so that we can control the onSelect for a better UX, especially for range mode
  const [selectedSingle, setSelectedSingle] = useState<Date | undefined>(
    props.mode === "single" ? props.selected : undefined,
  )
  const [selectedMultiple, setSelectedMultiple] = useState<Date[] | undefined>(
    props.mode === "multiple" ? props.selected : undefined,
  )
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    props.mode === "range" ? props.selected : undefined,
  )

  const onSelectSingle = useCallback<OnSelectHandler<Date>>(
    (selected, ...rest) => {
      setSelectedSingle(selected)
      if (props.mode === "single") {
        props.onSelect?.(selected, ...rest)
      }
    },
    [props],
  )
  const onSelectMultiple = useCallback<OnSelectHandler<Date[]>>(
    (selected, ...rest) => {
      setSelectedMultiple(selected)
      if (props.mode === "multiple") {
        props.onSelect?.(selected, ...rest)
      }
    },
    [props],
  )

  const onSelectRange = useCallback<OnSelectHandler<DateRange>>(
    (selected, clickedDate, ...rest) => {
      let newSelected = selected
      if (
        (selectedRange?.from != null && selectedRange.to != null) ||
        (selectedRange?.from == null && selectedRange?.to == null)
      ) {
        newSelected = { from: clickedDate, to: undefined }
      }
      setSelectedRange(newSelected)
      if (props.mode === "range") {
        props.onSelect?.(newSelected, clickedDate, ...rest)
      }
    },
    [props, selectedRange?.from, selectedRange?.to],
  )

  useEffect(() => {
    match(props)
      .with({ mode: "single", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedSingle((prev) => (prev !== selected ? selected : prev))
      })
      .with({ mode: "range", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedRange((prev) =>
          prev?.to !== selected?.to || prev?.from !== selected?.from ? selected : prev,
        )
      })
      .with({ mode: "multiple", onSelect: P.nonNullable }, ({ selected }) => {
        setSelectedMultiple((prev) =>
          prev?.length !== selected?.length ||
          selected?.some((date, i) => date !== prev?.[i])
            ? selected
            : prev,
        )
      })
      .otherwise(() => null)
  }, [props])

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex relative flex-wrap max-w-fit space-x-4",
        month: "space-y-4 first-of-type:!ml-0",
        month_caption:
          "flex items-center relative justify-center h-7 font-medium text-body1 mx-8",
        caption_label: "text-body1 font-medium z-[1] inline-flex items-center",
        dropdowns: "inline-flex items-center gap-2",
        dropdown: "opacity-0 w-full appearance-none absolute z-[2]",
        dropdown_root: "relative",
        nav: "flex items-center h-7 absolute w-full",
        button_previous: "absolute left-1 size-7 flex items-center justify-center",
        button_next: "absolute right-1 size-7 flex items-center justify-center",
        month_grid: "w-full border-collapse space-y-1",
        weekday: "text-muted-foreground rounded-md w-6 font-bold text-body1",
        day: "text-center text-body1 relative p-0 [&.day-range-start_button]:rounded-r-none [&.day-range-start.day-range-end_button]:rounded-md [&.day-range-end_button]:rounded-l-none [&.day-range-middle_button]:rounded-none",
        selected:
          "[&_button]:bg-primary-foreground [&.day-range-middle_button]:bg-primary-foreground/50 text-body1 font-bold aria-selected:text-white",
        range_end: "day-range-end day-range-edge",
        range_start: "day-range-start day-range-edge",
        range_middle: "day-range-middle",
        day_button: "size-8 my-px rounded-md",
        outside: "text-muted-foreground/50 aria-selected:text-muted-foreground",
        today: "text-primary-foreground font-bold",
        disabled: "opacity-50",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron({ orientation }: { orientation?: "up" | "down" | "left" | "right" }) {
          if (orientation === "up") return <IconChevronUp size={20} />
          if (orientation === "down") return <IconChevronDown size={20} />
          if (orientation === "left") return <IconChevronLeft size={20} />
          return <IconChevronRight size={20} />
        },
      }}
      {...props}
      {...match(props)
        .with({ mode: "single" }, () => ({
          onSelect: onSelectSingle,
          selected: selectedSingle,
        }))
        .with({ mode: "multiple" }, () => ({
          onSelect: onSelectMultiple,
          selected: selectedMultiple,
        }))
        .with({ mode: "range" }, () => ({
          onSelect: onSelectRange,
          selected: selectedRange,
        }))
        .otherwise(() => ({}))}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
